<template>
  <v-layout v-if="notSupported" column>
    <v-flex>
      <div class="screen-title component mb-0">
        <span>{{ adminText('shellAll.camera_not_found', 'Camera not found') }}</span>
      </div>
    </v-flex>
    <v-flex>
      <v-img :src="getImageData()" height="96" width="96" />
    </v-flex>
    <v-flex>
      <p
        class="body-1 mb-0"
      >
        {{ adminText('shellAll.make_sure_your_camera_is_connected_and_installed_properly_that_it_isn_t_being_blocked_by_antivirus_software_and_that_your_camera_drivers_are_up_to_date_please_check_and_try_again', 'Make sure your camera is connected and installed properly, that it isn\'t being blocked by antivirus software and that your camera drivers are up to date. Please check and try again.') }}
      </p>
    </v-flex>
    <v-flex>
      <v-btn color="primary" depressed class="ml-0 acc-btn" @click="cancel()">{{ adminText('shellAll.go_back', 'Go back') }}</v-btn>
    </v-flex>
  </v-layout>
  <v-layout v-else column>
    <v-flex>
      <v-select
        :value="selectedCamera"
        :items="cameras"
        :disabled="!!image || cameras.length === 1"
        item-text="label"
        item-value="deviceId"
        :label="adminText('shellAll.choose_camera', 'Choose camera')"
        @change="onCameraChange"
      />
    </v-flex>
    <div style="position: relative;" class="camera-container">
      <canvas v-if="!image" ref="canvas" class="canvas" :width="640" :height="480" />
      <img v-else :src="image" width="384" height="480">
      <video
        ref="video"
        height="480"
        width="640"
        class="hidden"
        muted
        autoplay
      />
      <div v-if="!image" style="height: 480px; position: absolute; left: 670px; top: 0px" xs6>
        <v-layout column wrap align-center>
          <v-flex>
            <v-btn icon :disabled="zoomOptions.indexOf(zoomRatio) == 0" @click.prevent="zoomOut">
              <v-icon medium color="primary">zoom_out</v-icon>
            </v-btn>
          </v-flex>
          <v-flex>
            <vue-slider
              ref="slider"
              v-model="zoomRatio"
              direction="ttb"
              :data="zoomOptions"
              tooltip="none"
              use-keyboard
              :height="390"
            />
          </v-flex>
          <v-flex>
            <v-btn
              icon
              :disabled="zoomOptions.indexOf(zoomRatio) == zoomOptions.length-1"
              @click.prevent="zoomIn"
            >
              <v-icon medium color="primary">zoom_in</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
      </div>
      <div v-if="!image" class="camera-overlay camera-overlay-left" />
      <div v-if="!image" class="camera-overlay camera-overlay-right" />
    </div>
    <v-flex>
      <main-button v-if="!image" depressed :text="adminText('shellAll.take_a_photo', 'Take a photo')" @click="capture()" />
      <main-button v-if="image" depressed :disabled="saving" :text="adminText('shellAll.save', 'Save')" @click="savePhoto()" />

      <main-button v-if="image" :disabled="saving" :text="adminText('shellAll.take_another', 'Take another')" flat @click="takeAnother()" />
      <main-button flat :text="adminText('shellAll.cancel', 'Cancel')" @click="cancel()" />
    </v-flex>
  </v-layout>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import { isSafari, frontendURL } from './../../utils'
import VueSlider from 'vue-slider-component'

export default {
  mixins: [AdminContext],
  name: 'camera',
  components: {
    VueSlider,
  },
  data() {
    const zoomRatio = parseFloat(localStorage.getItem('zoom')) || 1

    return {
      selectedCamera: {},
      cameras: [],
      saving: false,
      zoomRatio,
      zoomOptions: [1, 1.2, 1.4, 1.6, 1.8, 2.0, 2.2, 2.4, 2.6, 2.8, 3.0],
      image: null,
      notSupported: false,
    }
  },
  watch: {
    zoomRatio(value) {
      localStorage.setItem('zoom', value)
    },
  },
  async mounted() {
    await this.initComponent()
  },
  methods: {
    updateZoomRatio(index) {
      const zoomRatio = this.zoomOptions[index % this.zoomOptions.length]
      this.zoomRatio = zoomRatio
    },
    zoomIn() {
      const index = this.zoomOptions.indexOf(this.zoomRatio)
      this.updateZoomRatio(index + 1)
    },
    zoomOut() {
      const index = this.zoomOptions.indexOf(this.zoomRatio)
      this.updateZoomRatio(index - 1)
    },
    async initComponent() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(({ kind }) => kind === 'videoinput')
        this.cameras = videoDevices
        const deviceId = localStorage.getItem('camera')
        const foundDevice = videoDevices.find(device => device.deviceId === deviceId)
        const selectedDevice = foundDevice ? foundDevice : videoDevices[0]
        this.selectedCamera = selectedDevice

        this.loop()
        this.$refs.slider.focus()
      } catch (e) {
        this.notSupported = true
      }
    },
    loop() {
      const canvas = this.$refs.canvas
      const ctx = canvas && canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(this.$refs.video, 0, 0)
        ctx.setTransform(
          this.zoomRatio,
          0,
          0,
          this.zoomRatio,
          (-(this.zoomRatio - 1) * canvas.width) / 2,
          (-(this.zoomRatio - 1) * canvas.height) / 2,
        )
      }
      window.requestAnimationFrame(this.loop)
    },
    async onCameraChange(device) {
      try {
        this.stopCamera()
        const deviceId = device.deviceId ? device.deviceId : device
        localStorage.setItem('camera', deviceId)
        let videoConstraint = {
          deviceId,
        }
        if (isSafari()) {
          videoConstraint.width = 640
        }
        const selectedCamera = await navigator.mediaDevices.getUserMedia({ video: videoConstraint })
        await this.setCamera(selectedCamera)
      } catch (e) {
        this.notSupported = true
      }
    },
    stopCamera() {
      if (this.$refs.video && this.$refs.video.srcObject) {
        const t = this.$refs.video.srcObject.getTracks()[0]
        t.stop()
      }
    },
    async setCamera(selectedDevice) {
      await selectedDevice.getTracks()[0].applyConstraints({
        width: 640,
        height: 480,
      })
      this.$refs.video.srcObject = selectedDevice
      this.$refs.video.play()
    },
    cancel() {
      this.stopCamera()
      this.image = null
      this.$emit('cancel')
    },
    takeAnother() {
      this.image = null
    },
    capture() {
      const canvas = this.$refs.canvas
      const image = canvas.getContext('2d').getImageData(128, 0, 384, 480)

      const canvas2 = document.createElement('canvas')
      canvas2.width = 384
      canvas2.height = 480
      canvas2.getContext('2d').putImageData(image, 0, 0)
      this.image = canvas2.toDataURL()
      canvas2.remove()
    },
    getImageData() {
      return `${frontendURL}/assets/camera-off.svg`
    },
    savePhoto() {
      this.saving = true
      this.stopCamera()
      this.$emit('save', this.image)
    },
  },
}
</script>

<style scoped>
.hidden {
  display: none;
}
.camera-container {
  position: relative;
  min-height: 480px;
}

.camera-overlay {
  width: 128px;
  height: 480px;
  background: rgba(0, 0, 0, 0.8);
  position: absolute;
  top: 0;
}

.camera-overlay-left {
  left: 0px;
}

.camera-overlay-right {
  left: 512px;
}
</style>
